import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { getServerMastersList, saveServerMaster } from '../../utilities/api';
import '../CategoryMaster.css';
import '../../custom.css';

const FIELD_HEADER_MAP = {
    sectionId: 'Section ID',
    roleId: 'Role ID',
    uomId: 'UOM ID',
    taxId: 'Tax ID',
    platform_id: 'Platform ID',
    master_id: 'Master ID',
    message_type_id: 'Message Type ID',
    setting_id: 'Setting ID',
    supplier_id: 'Supplier ID',
    section_name: 'Section Name',
    section_remarks: 'Remarks',
    section_status: 'Status',
    role_status: 'Status',
    uom_status: 'Status',
    tax_status: 'Status',
    platform_status: 'Status',
    master_status: 'Status',
    message_type_status: 'Status',
    setting_status: 'Status',
    supplier_status: 'Status',
    uom_name: 'UOM Name',
    tax_name: 'Tax Name',
    tax_percentage: 'Tax %',
    platform_name: 'Platform Name',
    master_name: 'Master Name',
    message_type_name: 'Message Type',
    message_type: 'Message Type',
    setting_name: 'Setting Name',
    supplier_name: 'Supplier Name',
    supplier_mob: 'Mobile',
    supplier_address: 'Address',
    supplier_joining_date: 'Joining Date',
    role: 'Role Name',
    effective_from: 'Effective From'
};

const colHeader = (key) =>
    FIELD_HEADER_MAP[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' }
];

const tables = [
    { label: 'Sections', value: 'sections', pk: 'sectionId', icon: 'pi pi-th-large' },
    { label: 'Roles', value: 'roles', pk: 'roleId', icon: 'pi pi-users' },
    { label: 'UOM', value: 'uom_creation', pk: 'uomId', icon: 'pi pi-box' },
    { label: 'Tax', value: 'tax_creation', pk: 'taxId', icon: 'pi pi-percentage' },
    { label: 'Platforms', value: 'platform', pk: 'platform_id', icon: 'pi pi-desktop' },
    { label: 'Master', value: 'master', pk: 'master_id', icon: 'pi pi-database' },
    { label: 'Message Type', value: 'message_type', pk: 'message_type_id', icon: 'pi pi-envelope' },
    { label: 'Setting', value: 'setting', pk: 'setting_id', icon: 'pi pi-cog' },
    { label: 'Supplier', value: 'supplier', pk: 'supplier_id', icon: 'pi pi-truck' }
];

const VALIDATION_RULES = {
    sections: {
        section_name: { required: true, maxLength: 50, label: 'Section Name' },
        section_remarks: { required: false, maxLength: 255, label: 'Section Remarks' },
        section_status: { required: true, label: 'Status' }
    },
    roles: {
        role: { required: true, maxLength: 255, label: 'Role' },
        role_status: { required: true, label: 'Status' }
    },
    uom_creation: {
        uom_name: { required: true, maxLength: 255, label: 'UOM Name' },
        uom_status: { required: true, label: 'Status' }
    },
    tax_creation: {
        tax_name: { required: true, maxLength: 255, label: 'Tax Name' },
        tax_percentage: { required: true, type: 'number', label: 'Tax Percentage' },
        tax_status: { required: true, label: 'Status' }
    },
    platform: {
        platform_name: { required: true, maxLength: 255, label: 'Platform Name' },
        platform_status: { required: true, label: 'Status' }
    },
    master: {
        master_name: { required: true, maxLength: 255, label: 'Master Name' },
        master_status: { required: true, label: 'Status' }
    },
    message_type: {
        message_type_name: { required: true, maxLength: 255, label: 'Message Type Name' },
        message_type_status: { required: true, label: 'Status' }
    },
    setting: {
        setting_name: { required: true, maxLength: 255, label: 'Setting Name' },
        setting_status: { required: true, label: 'Status' }
    },
    supplier: {
        supplier_name: { required: true, maxLength: 255, label: 'Supplier Name' },
        supplier_mob: { required: false, maxLength: 255, type: 'mobile', label: 'Mobile Number' },
        supplier_address: { required: false, maxLength: 255, label: 'Address' },
        supplier_joining_date: { required: false, type: 'date', label: 'Joining Date' },
        supplier_status: { required: true, label: 'Status' }
    }
};

const UNIQUE_FIELDS = {
    sections: ['section_name'],
    roles: ['role'],
    uom_creation: ['uom_name'],
    tax_creation: ['tax_name'],
    platform: ['platform_name'],
    master: ['master_name'],
    message_type: ['message_type_name'],
    setting: ['setting_name'],
    supplier: ['supplier_name']
};

const STATUS_FIELD = {
    sections: 'section_status',
    roles: 'role_status',
    uom_creation: 'uom_status',
    tax_creation: 'tax_status',
    platform: 'platform_status',
    master: 'master_status',
    message_type: 'message_type_status',
    setting: 'setting_status',
    supplier: 'supplier_status'
};

const Parentmasters = () => {
    const [active, setActive] = useState('roles');
    const [data, setData] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [item, setItem] = useState({});
    const [errors, setErrors] = useState({});
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const toast = useRef(null);

    const cur = tables.find((t) => t.value === active) || tables[1];

    const statusField = STATUS_FIELD[active];
    const hasStatusField = data.length > 0 && statusField && data[0][statusField] !== undefined;
    const filteredData = statusFilter && hasStatusField ? data.filter((row) => row[statusField] === statusFilter) : data;
    const activeCount = hasStatusField ? data.filter((r) => r[statusField] === 'Active').length : 0;
    const inactiveCount = hasStatusField ? data.filter((r) => r[statusField] === 'Inactive').length : 0;

    const visibleKeys = data.length > 0 ? Object.keys(data[0]).filter((k) => !['created', 'updated'].includes(k.toLowerCase())) : [];
    const formKeys = Object.keys(VALIDATION_RULES[active] || {});

    useEffect(() => {
        setData([]);
        setGlobalFilterValue('');
        setStatusFilter(null);
        setItem({});
        setErrors({});
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
        loadData(active);
    }, [active]);

    useEffect(() => {
        loadPlatforms();
    }, []);

    const loadData = async (t) => {
        setLoading(true);
        try {
            const j = await getServerMastersList(t);
            setData(Array.isArray(j) ? j : []);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: `Could not load ${t}` });
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const loadPlatforms = async () => {
        try {
            const j = await getServerMastersList('platform');
            if (Array.isArray(j)) setPlatforms(j.map((p) => ({ label: p.platform_name, value: p.platform_id })));
        } catch (error) {
            console.error('Error loading platforms:', error);
        }
    };

    const validateForm = () => {
        const validationRules = VALIDATION_RULES[active] || {};
        const newErrors = {};

        Object.keys(validationRules).forEach((field) => {
            const rule = validationRules[field];
            const value = item[field];

            if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
                newErrors[field] = `${rule.label} is required`;
                return;
            }
            if (!value || (typeof value === 'string' && !value.trim())) return;
            if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
                newErrors[field] = `${rule.label} must not exceed ${rule.maxLength} characters`;
            }
            if (rule.type === 'number' && isNaN(parseFloat(value))) {
                newErrors[field] = `${rule.label} must be a valid number`;
            }
            if (rule.type === 'mobile' && value && value !== '0') {
                const mobileRegex = /^[0-9]{10,15}$/;
                if (!mobileRegex.test(value.replace(/[\s\-\+]/g, ''))) {
                    newErrors[field] = `${rule.label} must be a valid phone number`;
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const checkFieldDuplicate = (key, value) => {
        const uniqueFields = UNIQUE_FIELDS[active] || [];
        if (!uniqueFields.includes(key)) return;
        if (!value || (typeof value === 'string' && !value.trim())) return;
        const isDuplicate = data.some((row) => {
            if (item[cur.pk] && String(row[cur.pk]) === String(item[cur.pk])) return false;
            return String(row[key]).toLowerCase().trim() === String(value).toLowerCase().trim();
        });
        const fieldLabel = VALIDATION_RULES[active]?.[key]?.label || key;
        if (isDuplicate) {
            setErrors((prev) => ({ ...prev, [key]: `${fieldLabel} "${value}" already exists` }));
        } else {
            setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
        }
    };

    const checkDuplicates = () => {
        const uniqueFields = UNIQUE_FIELDS[active] || [];
        const dupErrors = {};
        for (const field of uniqueFields) {
            const value = item[field];
            if (!value || (typeof value === 'string' && !value.trim())) continue;
            const isDuplicate = data.some((row) => {
                if (item[cur.pk] && String(row[cur.pk]) === String(item[cur.pk])) return false;
                return String(row[field]).toLowerCase().trim() === String(value).toLowerCase().trim();
            });
            if (isDuplicate) {
                const fieldLabel = VALIDATION_RULES[active]?.[field]?.label || field;
                dupErrors[field] = `${fieldLabel} "${value}" already exists`;
            }
        }
        if (Object.keys(dupErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...dupErrors }));
            return false;
        }
        return true;
    };

    const confirmSave = () => {
        const isValid = validateForm();
        const isUnique = checkDuplicates();

        if (!isValid || !isUnique) {
            toast.current?.show({
                severity: 'error',
                summary: !isUnique && isValid ? 'Duplicate Record' : 'Validation Error',
                detail: !isUnique && isValid
                    ? 'A record with the same name already exists.'
                    : 'Please fix the highlighted fields before saving.',
                life: 5000
            });
            return;
        }

        confirmDialog({
            message: item[cur.pk] ? `Are you sure you want to update this ${cur.label}?` : `Are you sure you want to add this new ${cur.label}?`,
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => saveItem(),
            reject: () => toast.current?.show({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled' }),
            acceptLabel: 'Yes, Proceed',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text'
        });
    };

    const saveItem = async () => {
        try {
            const payload = { ...item };
            if (payload.supplier_joining_date instanceof Date) {
                payload.supplier_joining_date = moment(payload.supplier_joining_date).format('YYYY-MM-DD');
            }
            const j = await saveServerMaster(active, payload);
            if (j.status === 'success') {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Record saved successfully' });
                setVisible(false);
                setItem({});
                setErrors({});
                loadData(active);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: j.message || 'Save failed' });
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Save failed' });
        }
    };

    const handleResetFilters = () => {
        setGlobalFilterValue('');
        setStatusFilter(null);
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    };

    const exportToExcel = () => {
        const exportData = filteredData.map((row, i) => {
            const obj = { '#': i + 1 };
            visibleKeys.forEach((key) => {
                const lk = key.toLowerCase();
                let val = row[key];
                if (lk.includes('platform') && active !== 'platform') {
                    const p = platforms.find((p) => String(p.value) === String(val));
                    val = p ? p.label : val;
                } else if (lk.includes('date') && val) {
                    val = moment(val).isValid() ? moment(val).format('DD-MM-YYYY') : val;
                }
                obj[colHeader(key)] = val ?? '';
            });
            return obj;
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, cur.label.substring(0, 31));
        XLSX.writeFile(wb, `${cur.label.replace(/\s+/g, '_')}_${moment().format('DDMMYYYY')}.xlsx`);
    };

    const renderCell = (key, row) => {
        const lk = key.toLowerCase();
        if (lk.includes('platform') && active !== 'platform') {
            const p = platforms.find((p) => String(p.value) === String(row[key]));
            return p ? p.label : row[key];
        }
        if (lk.includes('status')) {
            const isActive = row[key] === 'Active';
            return <Tag value={row[key]} severity={isActive ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;
        }
        if (lk.includes('date') && row[key]) {
            return moment(row[key]).isValid() ? moment(row[key]).format('DD-MM-YYYY') : row[key];
        }
        return row[key];
    };

    const actionBodyTemplate = (rd) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => {
                const editData = { ...rd };
                if (editData.supplier_joining_date) {
                    editData.supplier_joining_date = moment(editData.supplier_joining_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).toDate();
                }
                setItem(editData);
                setErrors({});
                setVisible(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    const renderFormField = (key) => {
        const lk = key.toLowerCase();
        const isPlatform = lk.includes('platform');
        const isStatus = lk.includes('status');
        const isDate = lk.includes('date');
        const error = errors[key];
        const value = item[key];
        const rule = VALIDATION_RULES[active]?.[key];
        const label = rule?.label || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        const isNumber = rule?.type === 'number';
        const isMobile = rule?.type === 'mobile';
        const isUnique = (UNIQUE_FIELDS[active] || []).includes(key);

        const handleChange = (val) => {
            setItem((prev) => ({ ...prev, [key]: val }));
            if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
        };

        return (
            <div className="field-group" key={key}>
                <label className="field-label">
                    {label}
                    {rule?.required && <span className="required-mark"> *</span>}
                </label>
                {isStatus ? (
                    <Dropdown
                        value={value}
                        options={statusOptions}
                        onChange={(e) => handleChange(e.value)}
                        placeholder="Select Status"
                        className={error ? 'p-invalid' : ''}
                    />
                ) : isPlatform && active !== 'platform' ? (
                    <Dropdown
                        value={value}
                        options={platforms}
                        onChange={(e) => handleChange(e.value)}
                        placeholder="Select Platform"
                        filter
                        className={error ? 'p-invalid' : ''}
                    />
                ) : isDate ? (
                    <Calendar
                        value={value instanceof Date ? value : null}
                        onChange={(e) => handleChange(e.value)}
                        dateFormat="dd-mm-yy"
                        placeholder="DD-MM-YYYY"
                        showIcon
                        className={error ? 'p-invalid' : ''}
                    />
                ) : (
                    <InputText
                        value={value || ''}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={(e) => isUnique && checkFieldDuplicate(key, e.target.value)}
                        placeholder={`Enter ${label}`}
                        keyfilter={isNumber ? 'pnum' : isMobile ? 'int' : undefined}
                        maxLength={rule?.maxLength}
                        className={error ? 'p-invalid' : ''}
                    />
                )}
                {error && (
                    <small className="error-message">
                        <i className="pi pi-exclamation-circle"></i> {error}
                    </small>
                )}
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="masters-page-header">
                <h2>Parent Masters</h2>
                <p>View and manage parent master records</p>
            </div>

            <div className="list-filter-bar">
                <div className="filter-field" style={{ flex: '1 1 240px', minWidth: '200px' }}>
                    <label htmlFor="module">Module</label>
                    <Dropdown id="module" value={active} options={tables} onChange={(e) => setActive(e.value)} optionLabel="label" optionValue="value" placeholder="Select Module" className="w-full" />
                </div>
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button icon="pi pi-refresh" className="p-button-secondary" tooltip="Reset Filters" tooltipOptions={{ position: 'top' }} onClick={handleResetFilters} />
                </div>
            </div>

            <Card className="bill-reports-container">
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold m-0">{cur.label} List</h3>
                        <div className="category-status-tabs">
                            <button className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Active' ? null : 'Active')}>
                                <i className="pi pi-check-circle" />
                                Active <span className="tab-count">{activeCount}</span>
                            </button>
                            <button className={`status-tab-btn inactive-tab${statusFilter === 'Inactive' ? ' selected' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Inactive' ? null : 'Inactive')}>
                                <i className="pi pi-times-circle" />
                                Inactive <span className="tab-count">{inactiveCount}</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 align-items-center">
                        <Button icon="pi pi-file-excel" className="masters-excel-btn" onClick={exportToExcel} tooltip="Export to Excel" tooltipOptions={{ position: 'top' }} />
                        <span className="p-input-icon-left" style={{ minWidth: '200px' }}>
                            <i className="pi pi-search" />
                            <InputText
                                value={globalFilterValue}
                                onChange={(e) => {
                                    setGlobalFilterValue(e.target.value);
                                    setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                                }}
                                placeholder={`Search ${cur.label}...`}
                                className="w-full p-inputtext-sm"
                            />
                        </span>
                        <Button
                            label="Add New"
                            icon="pi pi-plus"
                            className="p-button-primary"
                            onClick={() => {
                                setItem({});
                                setErrors({});
                                setVisible(true);
                            }}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <div className="sub-masters-table-wrap">
                    <DataTable
                        value={filteredData}
                        filters={filters}
                        globalFilterFields={visibleKeys}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        showGridlines
                        tableStyle={{ minWidth: '40rem' }}
                        className="p-datatable-sm sub-masters-table"
                        emptyMessage={`No ${statusFilter ? statusFilter.toLowerCase() + ' ' : ''}${cur.label.toLowerCase()} records found`}
                    >
                        {visibleKeys.map((key) => (
                            <Column key={`${active}_${key}`} field={key} header={colHeader(key)} sortable body={(rd) => renderCell(key, rd)} />
                        ))}
                        <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                    </DataTable>
                    </div>
                )}
            </Card>

            <Dialog
                visible={visible}
                onHide={() => {
                    setVisible(false);
                    setItem({});
                    setErrors({});
                }}
                header={
                    <div className="sub-dialog-header">
                        <span className="sub-dialog-header-icon">
                            <i className={cur.icon} />
                        </span>
                        <div className="sub-dialog-header-text">
                            {item[cur.pk] ? `Edit ${cur.label}` : `Add ${cur.label}`}
                        </div>
                    </div>
                }
                style={{ width: '500px', maxWidth: '95vw' }}
                modal
                className="sub-master-dialog"
                draggable={false}
                resizable={false}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="sub-dialog-cancel"
                            onClick={() => {
                                setVisible(false);
                                setItem({});
                                setErrors({});
                            }}
                        />
                        <Button
                            label={item[cur.pk] ? 'Update' : 'Submit'}
                            icon="pi pi-check"
                            className="sub-dialog-save"
                            onClick={confirmSave}
                            disabled={Object.keys(errors).length > 0}
                        />
                    </div>
                }
            >
                <div className="dialog-content">{formKeys.map((key) => renderFormField(key))}</div>
            </Dialog>
        </>
    );
};

export default Parentmasters;
